import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import React from "react";
import { AuthProvider } from "./context/AuthContext";
import LoginPage from "./Authentication/LoginPage";
import SignupPage from "./Authentication/SignupPage";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import HomePage from "./HomePage";
import ContactUsPage from "./Pages/ContactUsPage";
import Dashboard from "./Pages/Dashboard";
import CreateCard from "./Pages/CreateCard";
import Profile from "./Pages/Profile";
import Streamers from "./Pages/Streamers";
import MyCards from "./Pages/MyCards";
import StreamerCards from "./Pages/StreamerCards";
import CardInventory from "./Pages/CardInventory";
import Challenges from "./Pages/Challenges";
import TermsAndConditions from "./Pages/TermsAndConditions";
import PrivacyPolicy from "./Pages/PrivacyPolicy";
import ScrollToTop from "./Components/ScrollToTop";
import { HelmetProvider } from 'react-helmet-async';
import ForgotPassword from "./Authentication/ForgotPassword";
import ResetPassword from "./Authentication/ResetPassword";
import BroadcastOverlay from "./Pages/BroadcastOverlay";
import StreamOverlay from "./Pages/StreamOverlay";
import PaymentCallback from "./Pages/PaymentCallback";

// Import admin components
import AdminLogin from "./Admin/AdminLogin";
import AdminForgotPassword from "./Admin/AdminForgotPassword";
import AdminDashboard from "./Admin/AdminDashboard";
import UserManagement from "./Admin/UserManagement";
import CardManagement from "./Admin/CardManagement";
import PaymentManagement from "./Admin/PaymentManagement";
import AdminProfile from "./Admin/AdminProfile";
import AdminSettings from "./Admin/AdminSettings";
import AdminResetPassword from "./Admin/AdminResetPassword";
import BroadcastCardManagement from './Admin/BroadcastCardManagement';
import DailyChallengeCards from './Admin/DailyChallengeCards';
import StreamerChallenge from './Admin/StreamerChallenge';
import TrendingCardsManagement from './Pages/TrendingCardsManagement';
import BroadcastDisplay from "./Admin/BroadcastDisplay";
import ProtectedRoute from "./Admin/components/ProtectedRoute";
import UserProtectedRoute from "./Components/ProtectedRoute";
import CreatorRoute from "./Components/CreatorRoute";
import Layout from "./Components/Layout";

// Import global styles last so they take precedence
import "./App.css";
import "./styles/modern-ui.css";
import "./styles/premium-theme-enhanced.css";

// ABSOLUTE NUCLEAR SOLUTION: Override toast.error BEFORE any other imports
// This runs immediately when the module starts loading
const originalToastError = require('react-toastify').toast.error;

// Nuclear override that blocks the exact "No token found" message
const nuclearToastOverride = (message, ...args) => {
  // Block the exact error message we're seeing
  if (message === "No token found. Please log in again.") {
    console.log('🚫 NUCLEAR BLOCK: Prevented "No token found" toast');
    return;
  }

  // Block any message containing these keywords during auth process
  const blockedKeywords = ['no token found', 'please log in again', 'session expired', 'unauthorized'];
  const shouldBlock = blockedKeywords.some(keyword =>
    message?.toLowerCase?.().includes?.(keyword)
  );

  if (shouldBlock) {
    console.log('🚫 BLOCKED auth toast:', message);
    return;
  }

  // Allow all other error messages
  return originalToastError(message, ...args);
};

// Apply override immediately
require('react-toastify').toast.error = nuclearToastOverride;

function App() {
  return (
    <Router>
      <HelmetProvider>
        <AuthProvider>
          <ScrollToTop />
          <Routes>
            {/* HomePage without Layout since it has its own header */}
            <Route path="/" element={<HomePage />} />

            {/* Public routes without Layout */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/contactus" element={<ContactUsPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* User routes with Layout */}
            <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
            <Route path="/create-card" element={<Layout><CreateCard /></Layout>} />
            <Route path="/profile" element={<Layout><Profile /></Layout>} />
            <Route path="/streamers" element={<Layout><Streamers /></Layout>} />
            <Route path="/streamers/cards" element={<Layout><StreamerCards /></Layout>} />
            <Route path="/streamers/:username/cards" element={<Layout><UserProtectedRoute redirectToLogin={true}><StreamerCards /></UserProtectedRoute></Layout>} />
            <Route path="/my-cards" element={<Layout><MyCards /></Layout>} />
            <Route path="/card-inventory" element={<Layout><CardInventory /></Layout>} />
            <Route path="/challenges" element={<Layout><Challenges /></Layout>} />
            <Route path="/tnc" element={<Layout><TermsAndConditions /></Layout>} />
            <Route path="/privacy" element={<Layout><PrivacyPolicy /></Layout>} />

            {/* Overlay routes without Layout */}
            <Route path="/overlay" element={
              <CreatorRoute>
                <BroadcastOverlay />
              </CreatorRoute>
            } />
            <Route path="/admin/overlay" element={
              <ProtectedRoute>
                <BroadcastOverlay />
              </ProtectedRoute>
            } />
            <Route path="/stream/:username" element={
              <UserProtectedRoute redirectToLogin={true}>
                <StreamOverlay />
              </UserProtectedRoute>
            } />
            <Route path="/payment-callback" element={<PaymentCallback />} />

            {/* Admin routes - Public (without Layout) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />

            {/* Admin routes - Protected (without Layout) */}
            <Route element={<ProtectedRoute />}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/user-management" element={<UserManagement />} />
              <Route path="/admin/card-management" element={<CardManagement />} />
              <Route path="/admin/broadcast-card-management" element={<BroadcastCardManagement />} />
              <Route path="/admin/daily-challenge-cards" element={<DailyChallengeCards />} />
              <Route path="/admin/streamer-challenges" element={<StreamerChallenge />} />
              <Route path="/admin/trending-cards" element={<TrendingCardsManagement />} />
              <Route path="/admin/payment-management" element={<PaymentManagement />} />
              <Route path="/admin/profile" element={<AdminProfile />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
            </Route>
          </Routes>
          <ToastContainer position="top-right" autoClose={2000} />
        </AuthProvider>
      </HelmetProvider>
    </Router>
  );
}

export default App;
